import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "../../i18n/useTranslation";
import { API_BASE_URL } from "../../config/apiBaseUrl";
import "./DigitalBookReader.css";
import type { BookPage, BookReaderView } from "./bookPageModel";
import { buildBookReaderViews, getStoryPageNumberForReaderView } from "./bookPageModel";
import { parseStoryParagraphs } from "../../utils/storyTextParsing";

const AUTO_HIDE_MS = 4000;
const FLIP_MS = 700;
const REDUCED_MS = 200;
/** When Auto next is on and a step has nothing to speak, advance after this delay */
const EMPTY_PAGE_AUTONEXT_MS = 1000;
/** After auto-advance navigation, wait this long after view images have loaded before speaking */
const POST_IMAGE_READ_DELAY_MS = 1000;
/** Minimum time after pressing Read aloud before speaking (Synth + title text ready more reliably). */
const MANUAL_READ_MIN_PREP_MS = 2000;

/** Softer bedside-story pacing (Chrome / Safari / Edge typically default ~1) */
const STORY_SPEECH_RATE = 0.9;
/** Very slight lift — higher values sound cartoonish on many engines */
const STORY_SPEECH_PITCH = 1.03;

function normalizeVoiceLang(lang: string): string {
  return lang.replace("_", "-").toLowerCase();
}

function speechVoiceMatchesPrimary(v: SpeechSynthesisVoice, primary: string): boolean {
  const p = primary.toLowerCase().split("-")[0];
  const h = normalizeVoiceLang(v.lang).split("-")[0];
  return h === p;
}

/** Prefer richer “natural / neural” system voices when the OS exposes them. */
function scoreStoryVoice(v: SpeechSynthesisVoice): number {
  const blob = `${v.name} ${v.voiceURI}`.toLowerCase();
  let s = 15;
  if (/(natural|neural|premium|enhanced| wavenet| studio)/i.test(blob)) s += 75;
  if (/\bonline\b/i.test(blob)) s += 40;
  if (/\bgoogle\b/i.test(blob)) s += 30;
  if (/\bamazon\b|\bpolly\b/i.test(blob)) s += 35;
  if (/\bmicrosoft\b/i.test(blob)) s += 12;
  if (/\bcompact\b|low.quality|low-quality/i.test(blob)) s -= 45;
  if (/\bwhisper|robot\b/i.test(blob)) s -= 25;
  try {
    if (v.localService === false) s += 12;
  } catch {
    /* ignore older engines */
  }
  if (typeof v.default === "boolean" && v.default) s += 4;
  return s;
}

function pickStoryReadingVoice(synth: SpeechSynthesis, bcp47: string): SpeechSynthesisVoice | undefined {
  const voices = synth.getVoices();
  if (!voices.length) return undefined;
  const primary = bcp47.split("-")[0] || "en";
  let pool = voices.filter((v) => speechVoiceMatchesPrimary(v, primary));
  if (!pool.length) pool = voices.filter((v) => normalizeVoiceLang(v.lang).startsWith(primary));
  if (!pool.length) pool = [...voices];

  let best = pool[0];
  let bestSc = scoreStoryVoice(pool[0]!);
  for (let i = 1; i < pool.length; i++) {
    const vv = pool[i];
    const sc = scoreStoryVoice(vv!);
    if (sc > bestSc) {
      bestSc = sc;
      best = vv;
    }
  }
  return best;
}

function preloadBookViewImages(urls: string[]): Promise<void> {
  if (urls.length === 0) return Promise.resolve();
  return Promise.all(
    urls.map(
      (src) =>
        new Promise<void>((resolve) => {
          const im = new Image();
          im.onload = () => resolve();
          im.onerror = () => resolve();
          im.src = src;
        })
    )
  ).then(() => undefined);
}

/** Used until intrinsic dimensions are known */
const DEFAULT_PAGE_AR = 16 / 9;
const SPREAD_GAP_PX = 8;
/** horizontal padding sum (8px + 8px on .book-spread-root) */
const SPREAD_HP_LR = 16;
/** vertical padding sum (6px + 6px on .book-spread-root) */
const SPREAD_V_SUM = 12;

/** Maximize book layout area: gutter + top (back link) + bottom (pagination + footer pill). Images stay `object-fit: contain`. */
const VIEW_MARGIN_X = 20;
const VIEW_TOP_RESERVE = 56;
/** Room for taller navbar pill on laptop+ (narrow chrome, extra vertical padding — see DigitalBookReader.css). */
const VIEW_BOTTOM_RESERVE = 252;

/** Match `.book-side-hit` clamp(56px, 12vw, 140px) so spreads don’t sit under arrows. */
function sideNavigationWidthReservePx(viewportW: number): number {
  const perSide = Math.min(140, Math.max(56, viewportW * 0.12));
  return 2 * perSide;
}

function viewportMaxBookBounds(viewportW: number, viewportH: number): { maxW: number; maxH: number } {
  const maxW = Math.max(viewportW - VIEW_MARGIN_X * 2 - sideNavigationWidthReservePx(viewportW), 240);
  const maxH = Math.max(viewportH - VIEW_TOP_RESERVE - VIEW_BOTTOM_RESERVE, 200);
  return { maxW, maxH };
}

function aspectRatioClamp(ar: number) {
  return Math.max(Number.isFinite(ar) ? ar : DEFAULT_PAGE_AR, 0.05);
}

/** Fit image (width/height = ar) wholly inside maxW × maxH; no cropping */
function fittedBoxFromAspect(ar: number, maxW: number, maxH: number): { width: number; height: number } {
  const a = aspectRatioClamp(ar);
  const mw = Math.max(maxW, 1);
  const mh = Math.max(maxH, 1);
  let w = mw;
  let h = w / a;
  if (h > mh) {
    h = mh;
    w = h * a;
  }
  return { width: w, height: h };
}

/** Row inner height inside spread (panels only — excludes outer vertical padding) */
function spreadRowInnerHeightPx(outerW: number, arL: number, arR: number): number {
  const innerFlex = Math.max(outerW - SPREAD_HP_LR, 0);
  const cw = Math.max((innerFlex - SPREAD_GAP_PX) / 2, 1);
  return Math.max(cw / aspectRatioClamp(arL), cw / aspectRatioClamp(arR));
}

/** Largest outer width that fits vertically, then derive height incl. tb padding */
function fittedSpreadOuterSize(arL: number, arR: number, maxOuterW: number, maxOuterH: number) {
  const hiMax = Math.max(maxOuterH - SPREAD_V_SUM, 44);
  const maxW = Math.max(Math.min(maxOuterW, 4096), 240);

  let lo = 240;
  let hi = maxW;

  while (hi - lo > 1) {
    const mid = Math.floor((lo + hi + 1) / 2);
    const rowInner = spreadRowInnerHeightPx(mid, arL, arR);
    if (rowInner <= hiMax) lo = mid;
    else hi = mid - 1;
  }

  let width = lo;
  while (width > 120 && spreadRowInnerHeightPx(width, arL, arR) > hiMax) width -= 8;

  const rowInner = Math.min(spreadRowInnerHeightPx(width, arL, arR), hiMax);
  const height = rowInner + SPREAD_V_SUM;
  return { width, height };
}

export interface DigitalBookReaderProps {
  pageSequence: BookPage[];
  storyLanguage?: string | undefined;
  sessionId: string;
  coverDisplayTitle?: string;
  generatedStory?: string | undefined;
  uiLocale: "en" | "he";
  setUiLocale: (locale: "en" | "he") => void;
  /** Spread / cover step index (0-based), not linear leaf index */
  pageIndex: number;
  setPageIndex: (idx: number) => void;
  exitHref: string;
  exitLabel: string;
  /** Admin-only: swap main/side images for the story spread currently on screen. */
  adminSpreadSwap?: {
    getAuthHeader: () => Record<string, string>;
    refetchStory: () => Promise<void>;
  };
}

function clampIndex(idx: number, len: number): number {
  if (len <= 0) return 0;
  return Math.min(Math.max(0, idx), len - 1);
}

function usePrefersReducedMotion(): boolean {
  const [rm, setRm] = useState(false);
  useEffect(() => {
    const m = window.matchMedia("(prefers-reduced-motion: reduce)");
    const fn = () => setRm(m.matches);
    fn();
    m.addEventListener("change", fn);
    return () => m.removeEventListener("change", fn);
  }, []);
  return rm;
}

function proseForBookPage(args: {
  page: BookPage | undefined;
  paragraphs: string[];
  coverDisplayTitle?: string;
}): string {
  const { page, paragraphs, coverDisplayTitle } = args;
  if (!page) return "";
  switch (page.type) {
    case "cover-front":
      return coverDisplayTitle?.trim() ? coverDisplayTitle.trim() : "";
    case "cover-back":
      return "";
    case "blank":
      return "";
    case "story": {
      const n = page.pageNumber;
      if (typeof n === "number" && paragraphs[n - 1]) return paragraphs[n - 1];
      return "";
    }
    default:
      return "";
  }
}

/** Read-aloud text for one reader step (spread = one story paragraph from main / recto page). */
function proseForReaderView(
  view: BookReaderView,
  sequence: BookPage[],
  paragraphs: string[],
  coverDisplayTitle?: string
): string {
  if (view.kind === "single") {
    return proseForBookPage({
      page: sequence[view.index],
      paragraphs,
      coverDisplayTitle,
    }).trim();
  }
  const mainPage = sequence[view.leftIndex];
  if (mainPage?.type === "story") {
    return proseForBookPage({ page: mainPage, paragraphs, coverDisplayTitle }).trim();
  }
  return "";
}

/** Image URLs that must load for a reader view (spread = up to two pages). */
function collectImageUrlsForBookView(v: BookReaderView | undefined, sequence: BookPage[]): string[] {
  if (!v) return [];
  if (v.kind === "single") {
    const url = sequence[v.index]?.imageUrl;
    return url ? [url] : [];
  }
  const a = sequence[v.leftIndex]?.imageUrl;
  const b = sequence[v.rightIndex]?.imageUrl;
  return [a, b].filter(Boolean) as string[];
}

const WaveBars: React.FC = () => (
  <span className="inline-flex items-end gap-[3px]" aria-hidden>
    {[5, 9, 6, 10, 7].map((h, i) => (
      <span
        key={i}
        className="w-[3px] rounded-sm bg-white"
        style={{
          height: h,
          animation: "bookWave 420ms ease-in-out infinite alternate",
          animationDelay: `${i * 80}ms`,
        }}
      />
    ))}
    <style>{`@keyframes bookWave { from { opacity: .35; transform: scaleY(.4); } to { opacity: 1; transform: scaleY(1); } }`}</style>
  </span>
);

const DigitalBookReader: React.FC<DigitalBookReaderProps> = ({
  pageSequence,
  storyLanguage,
  sessionId,
  coverDisplayTitle,
  generatedStory,
  uiLocale,
  setUiLocale,
  pageIndex,
  setPageIndex,
  exitHref,
  exitLabel,
  adminSpreadSwap,
}) => {
  const { t } = useTranslation();
  const reducedMotion = usePrefersReducedMotion();
  const [isSwappingSpreadSides, setIsSwappingSpreadSides] = useState(false);
  const fadeMs = reducedMotion ? REDUCED_MS : FLIP_MS;

  const views = useMemo(() => buildBookReaderViews(pageSequence), [pageSequence]);
  const total = views.length;
  const safeIndex = clampIndex(pageIndex, total);
  const currentView = total > 0 ? views[safeIndex] : undefined;

  const storyPageNumberForSwap = useMemo(
    () => getStoryPageNumberForReaderView(currentView, pageSequence),
    [currentView, pageSequence]
  );

  const paragraphs = useMemo(
    () => parseStoryParagraphs(storyLanguage, generatedStory),
    [storyLanguage, generatedStory]
  );
  const isRtlStory = storyLanguage === "Hebrew";
  /** Page-turn gestures and edge taps follow UI locale (Hebrew UI = forward on the left). */
  const navRtl = uiLocale === "he";

  const [isTransitioning, setIsTransitioning] = useState(false);
  const transitionTargetRef = useRef<number | null>(null);
  const [rmFade, setRmFade] = useState(false);

  const [chromeVisible, setChromeVisible] = useState(true);
  const hideTimerRef = useRef<number | null>(null);
  const [mouseInChromeZone, setMouseInChromeZone] = useState(false);

  const [viewport, setViewport] = useState(() => ({
    w: typeof window !== "undefined" ? window.innerWidth : 1200,
    h: typeof window !== "undefined" ? window.innerHeight : 900,
  }));

  const [aspectByUrl, setAspectByUrl] = useState<Record<string, number>>({});

  const [speaking, setSpeaking] = useState(false);
  const speakAfterTransitionPendingRef = useRef(false);
  const emptyPageAutoSkipTimerRef = useRef<number | null>(null);

  const clearEmptyPageAutoSkipTimer = useCallback(() => {
    if (emptyPageAutoSkipTimerRef.current == null) return;
    window.clearTimeout(emptyPageAutoSkipTimerRef.current);
    emptyPageAutoSkipTimerRef.current = null;
  }, []);

  const postTransitionSpeakTimerRef = useRef<number | null>(null);
  const postTransitionSpeakGenRef = useRef(0);

  const cancelPostTransitionSpeakSchedule = useCallback(() => {
    postTransitionSpeakGenRef.current += 1;
    if (postTransitionSpeakTimerRef.current != null) {
      window.clearTimeout(postTransitionSpeakTimerRef.current);
      postTransitionSpeakTimerRef.current = null;
    }
  }, []);

  const [autoAdvanceSpeech, setAutoAdvanceSpeech] = useState<boolean>(() => {
    try {
      return localStorage.getItem(`mysipur:book:${sessionId}:autoNext`) === "1";
    } catch {
      return false;
    }
  });

  const speakNowRef = useRef<(viewIdx: number) => void>(() => {});
  /** Set each render after `queueSpeakAfterImagesForIndex` is defined (used from goToViewIndex timeouts). */
  const queueSpeakAfterImagesRef = useRef<(viewIdx: number, requireAutoNextGate: boolean) => void>(() => {});
  const autoAdvanceSpeechRef = useRef(autoAdvanceSpeech);

  const pulseHideTimer = useCallback(() => {
    if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current);
    hideTimerRef.current = window.setTimeout(() => {
      if (!mouseInChromeZone) setChromeVisible(false);
      hideTimerRef.current = null;
    }, AUTO_HIDE_MS);
  }, [mouseInChromeZone]);

  const revealChromeTransient = useCallback(() => {
    setChromeVisible(true);
    pulseHideTimer();
  }, [pulseHideTimer]);

  const handleAdminSwapSpreadSides = useCallback(async () => {
    if (!adminSpreadSwap || storyPageNumberForSwap == null || isSwappingSpreadSides) return;
    setIsSwappingSpreadSides(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/stories/${encodeURIComponent(sessionId)}/swap-page-sides`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", ...adminSpreadSwap.getAuthHeader() },
          body: JSON.stringify({ pageNumber: storyPageNumberForSwap }),
        }
      );
      const payload = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(
          typeof payload?.message === "string" ? payload.message : "swap-page-sides failed"
        );
      }
      await adminSpreadSwap.refetchStory();
      revealChromeTransient();
    } catch (e) {
      console.error("DigitalBookReader: swap spread sides failed", e);
    } finally {
      setIsSwappingSpreadSides(false);
    }
  }, [
    adminSpreadSwap,
    isSwappingSpreadSides,
    sessionId,
    storyPageNumberForSwap,
    revealChromeTransient,
  ]);

  useEffect(() => {
    revealChromeTransient();
    return () => {
      if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (chromeVisible) pulseHideTimer();
  }, [chromeVisible, pulseHideTimer]);

  useEffect(() => {
    autoAdvanceSpeechRef.current = autoAdvanceSpeech;
  }, [autoAdvanceSpeech]);

  useEffect(() => {
    const sync = () =>
      setViewport({ w: window.innerWidth, h: window.innerHeight });
    sync();
    window.addEventListener("resize", sync);
    return () => window.removeEventListener("resize", sync);
  }, []);

  useEffect(() => {
    const synth = window.speechSynthesis;
    if (!synth?.getVoices) return undefined;
    const warm = () => {
      synth.getVoices();
    };
    warm();
    synth.addEventListener("voiceschanged", warm);
    return () => synth.removeEventListener("voiceschanged", warm);
  }, []);

  const ingestAspect = useCallback((url: string, naturalW: number, naturalH: number) => {
    if (!url || naturalW < 1 || naturalH < 1) return;
    const ar = naturalW / naturalH;
    setAspectByUrl((p) => (p[url] === ar ? p : { ...p, [url]: ar }));
  }, []);

  const goToViewIndex = useCallback(
    (next: number, opts?: { keepSpeechChainPending?: boolean; speechInterruptDelayMs?: number }) => {
      clearEmptyPageAutoSkipTimer();
      cancelPostTransitionSpeakSchedule();
      if (isTransitioning || next === safeIndex) return;
      if (next < 0 || next >= total) return;

      if (!opts?.keepSpeechChainPending) {
        speakAfterTransitionPendingRef.current = false;
      }

      revealChromeTransient();
      const synthSpeaking = !!(typeof window !== "undefined" && window.speechSynthesis?.speaking);
      const hadSpeechGoing = synthSpeaking || speaking;
      if (hadSpeechGoing) {
        window.speechSynthesis.cancel();
        setSpeaking(false);
      }
      const interruptDelayMs =
        opts?.speechInterruptDelayMs !== undefined ? opts.speechInterruptDelayMs : hadSpeechGoing ? 200 : 0;

      const run = () => {
        transitionTargetRef.current = next;
        setIsTransitioning(true);
        setRmFade(true);
        window.setTimeout(() => {
          const dest = transitionTargetRef.current ?? next;
          setPageIndex(clampIndex(dest, total));
          setIsTransitioning(false);
          setRmFade(false);
          transitionTargetRef.current = null;
          const sp = speakAfterTransitionPendingRef.current;
          speakAfterTransitionPendingRef.current = false;
          if (sp && autoAdvanceSpeechRef.current) {
            queueSpeakAfterImagesRef.current(clampIndex(dest, total), true);
          }
        }, fadeMs);
      };

      window.setTimeout(run, interruptDelayMs);
    },
    [
      cancelPostTransitionSpeakSchedule,
      clearEmptyPageAutoSkipTimer,
      fadeMs,
      isTransitioning,
      revealChromeTransient,
      safeIndex,
      setPageIndex,
      speaking,
      total,
    ]
  );

  const speakNow = useCallback(
    (viewIdx: number) => {
      cancelPostTransitionSpeakSchedule();
      clearEmptyPageAutoSkipTimer();
      const vi = clampIndex(viewIdx, total);
      const view = views[vi];
      if (!view) return;
      const text = proseForReaderView(view, pageSequence, paragraphs, coverDisplayTitle).trim();
      if (!text) {
        if (autoAdvanceSpeech) {
          const nn = Math.min(vi + 1, Math.max(0, total - 1));
          if (nn !== vi) {
            emptyPageAutoSkipTimerRef.current = window.setTimeout(() => {
              emptyPageAutoSkipTimerRef.current = null;
              speakAfterTransitionPendingRef.current = true;
              goToViewIndex(nn, { keepSpeechChainPending: true, speechInterruptDelayMs: 0 });
            }, EMPTY_PAGE_AUTONEXT_MS);
          }
        }
        return;
      }
      if (!window.speechSynthesis) return;
      window.speechSynthesis.cancel();
      const synth = window.speechSynthesis;
      const lang = isRtlStory ? "he-IL" : "en-US";
      const preferred = pickStoryReadingVoice(synth, lang);
      const u = new SpeechSynthesisUtterance(text);
      if (preferred) {
        u.voice = preferred;
        u.lang = preferred.lang || lang;
      } else {
        u.lang = lang;
      }
      u.rate = STORY_SPEECH_RATE;
      u.pitch = STORY_SPEECH_PITCH;
      u.volume = 1;
      u.onend = () => {
        setSpeaking(false);
        if (!autoAdvanceSpeech) return;
        const nn = Math.min(vi + 1, Math.max(0, total - 1));
        if (nn !== vi) {
          speakAfterTransitionPendingRef.current = true;
          goToViewIndex(nn, { keepSpeechChainPending: true, speechInterruptDelayMs: 0 });
        }
      };
      u.onerror = () => setSpeaking(false);
      window.speechSynthesis.speak(u);
      setSpeaking(true);
      try {
        localStorage.setItem(`mysipur:book:${sessionId}:t`, String(vi));
      } catch {
        /* ignore */
      }
    },
    [
      autoAdvanceSpeech,
      coverDisplayTitle,
      goToViewIndex,
      isRtlStory,
      paragraphs,
      pageSequence,
      sessionId,
      total,
      views,
      clearEmptyPageAutoSkipTimer,
      cancelPostTransitionSpeakSchedule,
    ]
  );

  speakNowRef.current = speakNow;

  const queueSpeakAfterImagesForIndex = useCallback(
    (viewIdx: number, requireAutoNextGate: boolean) => {
      const vi = clampIndex(viewIdx, total);
      const gen = postTransitionSpeakGenRef.current + 1;
      postTransitionSpeakGenRef.current = gen;
      if (postTransitionSpeakTimerRef.current != null) {
        window.clearTimeout(postTransitionSpeakTimerRef.current);
        postTransitionSpeakTimerRef.current = null;
      }
      const urls = collectImageUrlsForBookView(views[vi], pageSequence);

      const startSpeakAfterMs = (ms: number) => {
        postTransitionSpeakTimerRef.current = window.setTimeout(() => {
          postTransitionSpeakTimerRef.current = null;
          if (gen !== postTransitionSpeakGenRef.current) return;
          if (requireAutoNextGate && !autoAdvanceSpeechRef.current) return;
          speakNowRef.current(vi);
        }, ms);
      };

      /* Manual Read: wait ≥2s from button press and until images warmed (Synth/title flaky if faster). */
      if (!requireAutoNextGate) {
        void Promise.all([
          preloadBookViewImages(urls),
          new Promise<void>((resolve) => window.setTimeout(resolve, MANUAL_READ_MIN_PREP_MS)),
        ]).then(() => {
          if (gen !== postTransitionSpeakGenRef.current) return;
          speakNowRef.current(vi);
        });
        return;
      }

      void preloadBookViewImages(urls).then(() => {
        if (gen !== postTransitionSpeakGenRef.current) return;
        startSpeakAfterMs(POST_IMAGE_READ_DELAY_MS);
      });
    },
    [pageSequence, total, views]
  );

  queueSpeakAfterImagesRef.current = queueSpeakAfterImagesForIndex;

  const nextIdxFwd = total > 0 ? Math.min(safeIndex + 1, total - 1) : 0;
  const nextIdxBack = total > 0 ? Math.max(0, safeIndex - 1) : 0;

  useEffect(() => {
    const kb = (e: KeyboardEvent) => {
      if (isTransitioning) return;
      if (e.key === "ArrowRight") {
        e.preventDefault();
        goToViewIndex(navRtl ? nextIdxBack : nextIdxFwd);
      }
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goToViewIndex(navRtl ? nextIdxFwd : nextIdxBack);
      }
      revealChromeTransient();
    };
    window.addEventListener("keydown", kb);
    return () => window.removeEventListener("keydown", kb);
  }, [isTransitioning, goToViewIndex, navRtl, nextIdxBack, nextIdxFwd, revealChromeTransient]);

  const panRef = useRef<{ x: number; active: boolean }>({ x: 0, active: false });

  useEffect(() => {
    [safeIndex - 1, safeIndex, safeIndex + 1].forEach((vi) => {
      if (vi < 0 || vi >= views.length) return;
      collectImageUrlsForBookView(views[vi], pageSequence).forEach((u) => {
        const im = new Image();
        im.onload = () => ingestAspect(u, im.naturalWidth, im.naturalHeight);
        im.src = u;
      });
    });
  }, [pageSequence, safeIndex, views, ingestAspect]);

  const toggleReadAloud = useCallback(() => {
    revealChromeTransient();
    if (!window.speechSynthesis) return;
    speakAfterTransitionPendingRef.current = false;
    if (speaking) {
      cancelPostTransitionSpeakSchedule();
      clearEmptyPageAutoSkipTimer();
      window.speechSynthesis.cancel();
      setSpeaking(false);
      return;
    }
    if (!currentView) return;
    const text = proseForReaderView(currentView, pageSequence, paragraphs, coverDisplayTitle).trim();
    if (!text) return;
    cancelPostTransitionSpeakSchedule();
    clearEmptyPageAutoSkipTimer();
    queueSpeakAfterImagesRef.current(safeIndex, false);
  }, [
    revealChromeTransient,
    speaking,
    currentView,
    pageSequence,
    paragraphs,
    coverDisplayTitle,
    safeIndex,
    clearEmptyPageAutoSkipTimer,
    cancelPostTransitionSpeakSchedule,
  ]);

  const handleCenterTap = useCallback(() => {
    setChromeVisible((v) => !v);
  }, []);

  const fwdDisabled = isTransitioning || safeIndex >= total - 1;
  const backDisabled = isTransitioning || safeIndex <= 0;

  const showSidePrev = safeIndex > 0;
  const showSideNext = safeIndex < total - 1;

  const canReadPage =
    !!currentView && proseForReaderView(currentView, pageSequence, paragraphs, coverDisplayTitle).trim().length > 0;

  useEffect(() => {
    localStorage.setItem(`mysipur:book:${sessionId}:autoNext`, autoAdvanceSpeech ? "1" : "0");
  }, [autoAdvanceSpeech, sessionId]);

  useEffect(() => {
    if (!autoAdvanceSpeech) {
      clearEmptyPageAutoSkipTimer();
      cancelPostTransitionSpeakSchedule();
    }
  }, [autoAdvanceSpeech, cancelPostTransitionSpeakSchedule, clearEmptyPageAutoSkipTimer]);

  useEffect(
    () => () => {
      clearEmptyPageAutoSkipTimer();
      cancelPostTransitionSpeakSchedule();
    },
    [cancelPostTransitionSpeakSchedule, clearEmptyPageAutoSkipTimer]
  );

  const singleMaxBounds = useMemo(() => viewportMaxBookBounds(viewport.w, viewport.h), [viewport.h, viewport.w]);

  const fittedSinglePx = useMemo(() => {
    if (!currentView || currentView.kind !== "single") {
      return fittedBoxFromAspect(DEFAULT_PAGE_AR, singleMaxBounds.maxW, singleMaxBounds.maxH);
    }
    const p = pageSequence[currentView.index];
    const u = p?.imageUrl;
    const ar = u && aspectByUrl[u] !== undefined ? aspectByUrl[u]! : DEFAULT_PAGE_AR;
    return fittedBoxFromAspect(ar, singleMaxBounds.maxW, singleMaxBounds.maxH);
  }, [aspectByUrl, currentView, pageSequence, singleMaxBounds.maxH, singleMaxBounds.maxW]);

  const fittedSpreadPx = useMemo(() => {
    const { maxW, maxH } = viewportMaxBookBounds(viewport.w, viewport.h);
    if (!currentView || currentView.kind !== "spread") {
      return fittedSpreadOuterSize(DEFAULT_PAGE_AR, DEFAULT_PAGE_AR, maxW, maxH);
    }
    const leftPage = pageSequence[currentView.leftIndex];
    const rightPage = pageSequence[currentView.rightIndex];
    const lu = leftPage?.imageUrl;
    const ru = rightPage?.imageUrl;
    const arL = lu && aspectByUrl[lu] !== undefined ? aspectByUrl[lu]! : DEFAULT_PAGE_AR;
    const arR = ru && aspectByUrl[ru] !== undefined ? aspectByUrl[ru]! : DEFAULT_PAGE_AR;
    return fittedSpreadOuterSize(arL, arR, maxW, maxH);
  }, [aspectByUrl, currentView, pageSequence, viewport.h, viewport.w]);

  if (total === 0) {
    return (
      <div className="book-reader relative flex items-center justify-center text-white/80">
        <Link
          to={exitHref}
          className="absolute left-3 z-[20] rounded-xl border border-white/25 bg-black/35 px-3 py-2 text-sm font-semibold text-white backdrop-blur-sm hover:bg-black/50 focus:outline-none focus:ring-2 focus:ring-white/40"
          style={{ top: `max(0.75rem, env(safe-area-inset-top, 0px))` }}
        >
          {t("wizard.digitalBookView.back")}
        </Link>
        {exitLabel}
      </div>
    );
  }

  function spreadHalf(page: BookPage | undefined) {
    if (!page) {
      return (
        <div className="book-spread-panel flex items-center justify-center bg-neutral-950 text-white/35">
          —
        </div>
      );
    }
    if (page.type === "blank") {
      return <div className="book-spread-panel book-spread-panel--blank" role="presentation" />;
    }
    if (page.imageUrl) {
      return (
        <div className="book-spread-panel overflow-hidden rounded-sm bg-neutral-950">
          {/* eslint-disable-next-line jsx-a11y/alt-text */}
          <img
            className="book-img-spread"
            src={page.imageUrl}
            alt={page.accessibleDescription ?? ""}
            onLoad={(ev) =>
              ingestAspect(page.imageUrl!, ev.currentTarget.naturalWidth, ev.currentTarget.naturalHeight)
            }
          />
        </div>
      );
    }
    return (
      <div className="book-spread-panel flex items-center justify-center bg-neutral-950 text-white/35">
        —
      </div>
    );
  }

  function singleInner(page: BookPage | undefined, framePx: { width: number; height: number }) {
    const frame = "book-single-frame shadow-lg";
    const framing = { width: framePx.width, height: framePx.height };
    if (!page) {
      return (
        <div
          className={`${frame} flex items-center justify-center bg-neutral-950 text-white/35`}
          style={framing}
        >
          —
        </div>
      );
    }
    if (page.type === "blank") {
      return <div className={`${frame} bg-white`} role="presentation" style={framing} />;
    }
    if (page.imageUrl) {
      return (
        <div className={frame} style={framing}>
          {/* eslint-disable-next-line jsx-a11y/alt-text */}
          <img
            className="book-single-img absolute inset-0 h-full w-full object-contain object-center"
            src={page.imageUrl}
            alt={page.accessibleDescription ?? ""}
            onLoad={(ev) =>
              ingestAspect(page.imageUrl!, ev.currentTarget.naturalWidth, ev.currentTarget.naturalHeight)
            }
          />
        </div>
      );
    }
    return (
      <div
        className={`${frame} flex items-center justify-center bg-neutral-950 text-white/35`}
        style={framing}
      >
        —
      </div>
    );
  }

  const renderDots = (
    <div
      className="book-page-dots mx-auto mt-3 max-w-full overflow-x-auto px-4"
      role="toolbar"
      aria-label="Pages"
    >
      <span className="inline-flex shrink-0 gap-2">
        {views.map((_, dotIdx) => {
          const act = dotIdx === safeIndex;
          return (
            <button
              key={dotIdx}
              type="button"
              className={`book-dot ${act ? "book-dot--active" : ""}`}
              aria-label={`Page ${dotIdx + 1}`}
              aria-current={act ? "step" : undefined}
              disabled={isTransitioning}
              style={{ opacity: act ? 1 : 0.4 }}
              onClick={() => {
                if (dotIdx !== safeIndex) goToViewIndex(dotIdx);
                revealChromeTransient();
              }}
            />
          );
        })}
      </span>
    </div>
  );

  const renderStage = (
    <div className={`book-stage ${rmFade ? "book-stage--fade" : ""}`}>
      {currentView?.kind === "single" ? (
        <div className="book-single-outer">{singleInner(pageSequence[currentView.index], fittedSinglePx)}</div>
      ) : currentView?.kind === "spread" ? (
        <div
          className={`book-spread-root ${isRtlStory ? "book-spread-root--rtl" : ""}`}
          dir={isRtlStory ? "rtl" : "ltr"}
          style={{ width: fittedSpreadPx.width, height: fittedSpreadPx.height }}
        >
          {spreadHalf(pageSequence[currentView.leftIndex])}
          {spreadHalf(pageSequence[currentView.rightIndex])}
        </div>
      ) : null}
    </div>
  );

  return (
    <div
      className="book-reader touch-pan-y"
      role="application"
      aria-label="Digital book"
      onMouseMove={(e) => {
        const fromBottom = window.innerHeight - e.clientY;
        if (fromBottom <= 120) {
          setMouseInChromeZone(true);
          revealChromeTransient();
        } else {
          setMouseInChromeZone(false);
        }
      }}
      onPointerDown={(ev) => {
        panRef.current = { x: ev.clientX, active: true };
        revealChromeTransient();
      }}
      onPointerUp={(ev) => {
        if (!panRef.current.active) return;
        panRef.current.active = false;
        const dx = ev.clientX - panRef.current.x;
        if (Math.abs(dx) > window.innerWidth * 0.25) {
          let target = clampIndex(dx < 0 ? nextIdxFwd : nextIdxBack, total);
          if (navRtl) {
            target = clampIndex(dx < 0 ? nextIdxBack : nextIdxFwd, total);
          }
          goToViewIndex(target);
        }
      }}
      onPointerCancel={() => {
        panRef.current.active = false;
      }}
    >
      <button
        type="button"
        className="absolute left-0 right-0 top-0 z-[14] bg-transparent"
        style={{ bottom: 200 }}
        aria-label={chromeVisible ? "Hide controls" : "Show controls"}
        onClick={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          const mx = e.clientX - r.left;
          const my = e.clientY - r.top;
          const cx = r.width / 2;
          const cy = r.height / 2;
          const inCenterEllipse =
            Math.hypot((mx - cx) / (r.width * 0.42), (my - cy) / (r.height * 0.42)) < 1;
          if (!inCenterEllipse) revealChromeTransient();
          else handleCenterTap();
        }}
      />

      <Link
        to={exitHref}
        className="absolute left-3 z-[20] rounded-xl border border-white/25 bg-black/35 px-3 py-2 text-sm font-semibold text-white backdrop-blur-sm hover:bg-black/50 focus:outline-none focus:ring-2 focus:ring-white/40 pointer-events-auto"
        style={{ top: `max(0.75rem, env(safe-area-inset-top, 0px))` }}
        onClick={() => revealChromeTransient()}
      >
        {t("wizard.digitalBookView.back")}
      </Link>

      <div className="absolute inset-0 z-[1] flex items-center justify-center">{renderStage}</div>
      <div className="book-scrim pointer-events-none" />

      <div className="book-side-hit book-side-hit-left">
        <button
          type="button"
          className="book-side-chev"
          disabled={navRtl ? fwdDisabled || !showSideNext : backDisabled || !showSidePrev}
          aria-label={navRtl ? t("wizard.digitalBookView.nextPage") : t("wizard.digitalBookView.prevPage")}
          onClick={(e) => {
            e.stopPropagation();
            goToViewIndex(navRtl ? nextIdxFwd : nextIdxBack);
          }}
        >
          ◀
        </button>
      </div>
      <div className="book-side-hit book-side-hit-right">
        <button
          type="button"
          className="book-side-chev"
          disabled={navRtl ? backDisabled || !showSidePrev : fwdDisabled || !showSideNext}
          aria-label={navRtl ? t("wizard.digitalBookView.prevPage") : t("wizard.digitalBookView.nextPage")}
          onClick={(e) => {
            e.stopPropagation();
            goToViewIndex(navRtl ? nextIdxBack : nextIdxFwd);
          }}
        >
          ▶
        </button>
      </div>

      <footer
        className="book-chrome px-4"
        style={{ opacity: chromeVisible ? 1 : 0, pointerEvents: chromeVisible ? "auto" : "none" }}
      >
        <div className={`book-chrome-gap w-full`}>{renderDots}</div>
        <div className="book-pill w-full shadow-xl">
          <button
            type="button"
            className={`book-lang-btn ${uiLocale !== "he" ? "book-lang-btn--on" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              setUiLocale("en");
              revealChromeTransient();
            }}
          >
            EN
          </button>
          <button
            type="button"
            className={`book-lang-btn ${uiLocale === "he" ? "book-lang-btn--on" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              setUiLocale("he");
              revealChromeTransient();
            }}
          >
            עב
          </button>
          <button
            type="button"
            className="book-read-btn ml-2 flex items-center gap-2"
            disabled={!canReadPage}
            onClick={toggleReadAloud}
          >
            {!speaking ? (
              <>{uiLocale === "he" ? "קריאה בקול" : "Read aloud"}</>
            ) : (
              <>
                <WaveBars />
                {uiLocale === "he" ? "השהיה" : "Pause"}
              </>
            )}
          </button>
          <label className="book-chrome-checkbox-label ml-2 flex cursor-pointer items-center font-semibold text-white/85">
            <input
              type="checkbox"
              checked={autoAdvanceSpeech}
              onChange={(e) => {
                setAutoAdvanceSpeech(e.target.checked);
                revealChromeTransient();
              }}
            />
            {uiLocale === "he" ? "המשך אוטומטי" : "Auto next"}
          </label>
          {adminSpreadSwap && storyPageNumberForSwap != null ? (
            <button
              type="button"
              className="book-admin-swap-btn ml-2 shrink-0 rounded-lg border border-white/35 bg-white/12 px-2.5 py-1.5 font-semibold text-white hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-50"
              title={t("wizard.storyResult.swapSidesTitle", { page: storyPageNumberForSwap })}
              disabled={isSwappingSpreadSides || isTransitioning}
              onClick={(e) => {
                e.stopPropagation();
                void handleAdminSwapSpreadSides();
              }}
            >
              {isSwappingSpreadSides ? "…" : t("wizard.storyResult.swapSides")}
            </button>
          ) : null}
          <a className="book-mini-link ml-auto" href={exitHref} onClick={() => revealChromeTransient()}>
            {exitLabel}
          </a>
        </div>
      </footer>
    </div>
  );
};

export default DigitalBookReader;
